import React, { useEffect, useState } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import axios from 'axios'
import styles from './soperator.module.css'

const SpecificOperator = () => {
    const { id } = useParams()
    const operatorId = id;
    const location = useLocation()
    const navigate = useNavigate()

    const [operator, setOperator] = useState(null)
    const [packages, setPackages] = useState([])
    const [loading, setLoading] = useState(true)
    const [packagesLoading, setPackagesLoading] = useState(false)
    const [verifying, setVerifying] = useState(false)
    const [packageVerifying, setPackageVerifying] = useState(false)
    const [message, setMessage] = useState({ text: '', type: '' })
    const [modalMessage, setModalMessage] = useState({ text: '', type: '' }) // Separate message for modal
    const [selectedPackage, setSelectedPackage] = useState(null)
    const [showPackageModal, setShowPackageModal] = useState(false)

    // Get image_url directly from navigation state
    const imageUrlFromState = location.state?.image_url

    // Auto-hide messages after 3 seconds
    useEffect(() => {
        if (message.text) {
            const timer = setTimeout(() => {
                setMessage({ text: '', type: '' })
            }, 3000)
            return () => clearTimeout(timer)
        }
    }, [message.text])

    // Auto-hide modal messages after 3 seconds
    useEffect(() => {
        if (modalMessage.text) {
            const timer = setTimeout(() => {
                setModalMessage({ text: '', type: '' })
            }, 3000)
            return () => clearTimeout(timer)
        }
    }, [modalMessage.text])

    const fetchOperatorDetails = async () => {
        try {
            setLoading(true)
            const response = await axios.get(
                `${process.env.REACT_APP_URL}/tourism/operator/${operatorId}`,
                { withCredentials: true }
            )
            setOperator(response.data.operator)
        } catch (error) {
            console.error('Error fetching operator details:', error)
            setMessage({
                text: 'Failed to load operator details',
                type: 'error'
            })
        } finally {
            setLoading(false)
        }
    }

    const fetchOperatorPackages = async () => {
        try {
            setPackagesLoading(true)
            const response = await axios.get(
                `${process.env.REACT_APP_URL}/tourism/operator/packages/${operatorId}`,
                { withCredentials: true }
            )
            setPackages(response.data.packages || [])
            console.log('Operator packages data:', response.data.packages)
        } catch (error) {
            console.error('Error fetching operator packages:', error)
            // Don't show error message for packages, just log it
        } finally {
            setPackagesLoading(false)
        }
    }

    const verifyOperator = async (status) => {
        try {
            setVerifying(true)
            setMessage({ text: '', type: '' })

            const response = await axios.put(
                `${process.env.REACT_APP_URL}/tourism/operator/${operatorId}/verify`,
                {
                    is_verified: status,
                    verified_by: 'admin'
                },
                { withCredentials: true }
            )

            setOperator(prev => ({
                ...prev,
                is_verified: status
            }))

            setMessage({
                text: `Operator ${status === 1 ? 'verified' : 'unverified'} successfully`,
                type: 'success'
            })

        } catch (error) {
            console.error('Error verifying operator:', error)
            setMessage({
                text: error.response?.data?.message || 'Failed to update verification status',
                type: 'error'
            })
        } finally {
            setVerifying(false)
        }
    }

    // New function to verify package
    const verifyPackage = async (packageId, status) => {
        try {
            setPackageVerifying(true)
            setModalMessage({ text: '', type: '' })

            const response = await axios.put(
                `${process.env.REACT_APP_URL}/tourism/operator1/package/${packageId}/verify`,
                {
                    is_verified: status,
                    verified_by: 'admin'
                },
                { withCredentials: true }
            )

            // Update the package in local state
            setPackages(prevPackages =>
                prevPackages.map(pkg =>
                    pkg.package_id === packageId
                        ? { ...pkg, is_verified: status }
                        : pkg
                )
            )

            // Update selected package if it's the one being verified
            if (selectedPackage && selectedPackage.package_id === packageId) {
                setSelectedPackage(prev => ({
                    ...prev,
                    is_verified: status
                }))
            }

            setModalMessage({
                text: `Package ${status === 1 ? 'verified' : 'unverified'} successfully`,
                type: 'success'
            })

        } catch (error) {
            console.error('Error verifying package:', error)
            setModalMessage({
                text: error.response?.data?.message || 'Failed to update package verification status',
                type: 'error'
            })
        } finally {
            setPackageVerifying(false)
        }
    }

    const handleVerify = () => verifyOperator(1)
    const handleUnverify = () => verifyOperator(0)
    const handleVerifyPackage = () => {
        if (selectedPackage) {
            verifyPackage(selectedPackage.package_id, 1)
        }
    }
    const handleUnverifyPackage = () => {
        if (selectedPackage) {
            verifyPackage(selectedPackage.package_id, 0)
        }
    }

    const handleBack = () => {
        navigate(-1)
    }

    const handlePackageClick = (pkg) => {
        setSelectedPackage(pkg)
        setShowPackageModal(true)
        setModalMessage({ text: '', type: '' }) // Clear modal message when opening modal
    }

    const closePackageModal = () => {
        setShowPackageModal(false)
        setSelectedPackage(null)
        setModalMessage({ text: '', type: '' }) // Clear modal message when closing modal
    }

    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'INR'
        }).format(price || 0);
    }

    const formatDuration = (packageDuration, days) => {
        if (packageDuration) return packageDuration;
        if (days && days.length > 0) {
            const totalDays = days.length;
            return totalDays === 1 ? '1 day' : `${totalDays} days`;
        }
        return 'N/A';
    }

    const getPackageDuration = (pkg) => {
        return pkg.package_duration || (pkg.days && pkg.days.length > 0 ? `${pkg.days.length} days` : 'N/A');
    }

    const getMaxPeople = (pkg) => {
        return pkg.numPeople || 'N/A';
    }

    const getPackageCategory = (pkg) => {
        return pkg.packageCategory || 'N/A';
    }

    const getPackageLocation = (pkg) => {
        return pkg.primaryLocation || (pkg.location_coords && pkg.location_coords.formatted_address) || 'N/A';
    }

    const getPackageDescription = (pkg) => {
        return pkg.shortDescription || pkg.packageName || 'No description available';
    }

    const getPackageImage = (pkg) => {
        return pkg.logo_url || pkg.logo_image_url || null;
    }

    const getPackageRating = (pkg) => {
        return pkg.ratings ? parseFloat(pkg.ratings).toFixed(1) : 'No ratings';
    }

    const getPackageReviews = (pkg) => {
        return pkg.review_count || 0;
    }

    const getMealTypeDisplay = (mealType) => {
        return mealType.charAt(0).toUpperCase() + mealType.slice(1);
    }

    const getPackageVerificationStatus = (pkg) => {
        return pkg.is_verified ? 'Verified' : 'Pending Verification';
    }

    useEffect(() => {
        if (operatorId) {
            fetchOperatorDetails();
            fetchOperatorPackages();
        }
    }, [operatorId])

    // Close modal on escape key
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                closePackageModal();
            }
        }

        if (showPackageModal) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        }
    }, [showPackageModal])

    if (loading) {
        return (
            <div className={styles.container}>
                <div className={styles.loading}>Loading operator details...</div>
            </div>
        )
    }

    if (!operator) {
        return (
            <div className={styles.container}>
                <div className={styles.error}>Operator not found</div>
                <button onClick={handleBack} className={styles.backButton}>
                    Go Back
                </button>
            </div>
        )
    }

    return (
        <div className={styles.container}>
            {/* Header with Back Button */}
            <div className={styles.header}>
                <button onClick={handleBack} className={styles.backButton}>
                    ← Back
                </button>
                <h1 className={styles.title}>Operator Details</h1>
            </div>

            {/* Message Alert */}
            {message.text && (
                <div className={`${styles.alert} ${styles[message.type]}`}>
                    {message.text}
                </div>
            )}

            <div className={styles.content}>
                {/* Operator Profile Section */}
                <div className={styles.profileSection}>
                    <div className={styles.imageContainer}>
                        {imageUrlFromState || operator.image_url ? (
                            <img
                                src={imageUrlFromState || operator.image_url}
                                alt={operator.name}
                                className={styles.operatorImage}
                                onError={(e) => {
                                    e.target.style.display = 'none'
                                    e.target.nextSibling.style.display = 'flex'
                                }}
                            />
                        ) : null}

                        <div
                            className={styles.placeholderImage}
                            style={{
                                display: (imageUrlFromState || operator.image_url) ? 'none' : 'flex'
                            }}
                        >
                            {operator.name?.charAt(0)?.toUpperCase()}
                        </div>
                    </div>

                    <div className={styles.profileInfo}>
                        <div className={styles.nameRow}>
                            <h2 className={styles.operatorName}>{operator.name}</h2>
                            <span className={`${styles.verificationBadge} ${operator.is_verified ? styles.verified : styles.pending
                                }`}>
                                {operator.is_verified ? 'Verified' : 'Pending Verification'}
                            </span>
                        </div>

                        {/* Contact Information */}
                        <div className={styles.contactInfo}>
                            {operator.email && (
                                <div className={styles.contactItem}>
                                    <span className={styles.contactLabel}>Email:</span>
                                    <span className={styles.contactValue}>{operator.email}</span>
                                </div>
                            )}
                            {operator.phone && (
                                <div className={styles.contactItem}>
                                    <span className={styles.contactLabel}>Phone:</span>
                                    <span className={styles.contactValue}>{operator.phone}</span>
                                </div>
                            )}

                            {/* Verification Actions */}
                            <div className={styles.verificationActions}>
                                {!operator.is_verified ? (
                                    <button
                                        onClick={handleVerify}
                                        disabled={verifying}
                                        className={styles.verifyButton}
                                    >
                                        {verifying ? 'Verifying...' : 'Verify Operator'}
                                    </button>
                                ) : (
                                    <button
                                        onClick={handleUnverify}
                                        disabled={verifying}
                                        className={styles.unverifyButton}
                                    >
                                        {verifying ? 'Updating...' : 'Unverify Operator'}
                                    </button>
                                )}
                            </div>
                        </div>

                    </div>
                </div>

                {/* Operator Details Grid */}
                <div className={styles.detailsGrid}>
                    <div className={styles.detailCard}>
                        <h3 className={styles.detailTitle}>Business Information</h3>
                        <div className={styles.detailItem}>
                            <span className={styles.label}>Business Type:</span>
                            <span className={styles.value}>{operator.business_type || 'N/A'}</span>
                        </div>
                        <div className={styles.detailItem}>
                            <span className={styles.label}>Business Number:</span>
                            <span className={styles.value}>{operator.business_number || 'N/A'}</span>
                        </div>
                        <div className={styles.detailItem}>
                            <span className={styles.label}>Operating Since:</span>
                            <span className={styles.value}>{operator.operating_since || 'N/A'}</span>
                        </div>
                        <div className={styles.detailItem}>
                            <span className={styles.label}>Languages:</span>
                            <span className={styles.value}>{operator.languages || 'N/A'}</span>
                        </div>
                    </div>

                    <div className={styles.detailCard}>
                        <h3 className={styles.detailTitle}>Location & Contact</h3>
                        <div className={styles.detailItem}>
                            <span className={styles.label}>Location:</span>
                            <span className={styles.value}>{operator.location || operator.address || 'N/A'}</span>
                        </div>
                        <div className={styles.detailItem}>
                            <span className={styles.label}>Website:</span>
                            <span className={styles.value}>
                                {operator.website ? (
                                    <a href={operator.website} target="_blank" rel="noopener noreferrer" className={styles.link}>
                                        Visit Website
                                    </a>
                                ) : 'N/A'}
                            </span>
                        </div>
                        <div className={styles.detailItem}>
                            <span className={styles.label}>Response Time:</span>
                            <span className={styles.value}>{operator.response_time || 'N/A'}</span>
                        </div>
                    </div>

                    <div className={styles.detailCard}>
                        <h3 className={styles.detailTitle}>Ratings & Reviews</h3>
                        <div className={styles.detailItem}>
                            <span className={styles.label}>Rating:</span>
                            <span className={styles.value}>
                                ⭐ {operator.rating ? parseFloat(operator.rating).toFixed(1) : 'No ratings'}
                            </span>
                        </div>
                        <div className={styles.detailItem}>
                            <span className={styles.label}>Total Reviews:</span>
                            <span className={styles.value}>{operator.reviews || 0}</span>
                        </div>
                        {operator.social_links && operator.social_links.length > 0 && (
                            <div className={styles.detailItem}>
                                <span className={styles.label}>Social Links:</span>
                                <span className={styles.value}>
                                    {operator.social_links.map((link, index) => (
                                        <div key={index}>
                                            <a href={link} target="_blank" rel="noopener noreferrer" className={styles.link}>
                                                {link.includes('instagram') ? 'Instagram' :
                                                    link.includes('facebook') ? 'Facebook' :
                                                        link.includes('twitter') ? 'Twitter' : 'Social Link'}
                                            </a>
                                        </div>
                                    ))}
                                </span>
                            </div>
                        )}
                    </div>

                    <div className={styles.detailCard}>
                        <h3 className={styles.detailTitle}>Description</h3>
                        <p className={styles.description}>
                            {operator.description || 'No description provided.'}
                        </p>
                    </div>
                </div>

                {/* Packages Section */}
                <div className={styles.packagesSection}>
                    <div className={styles.sectionHeader}>
                        <h3 className={styles.detailTitle}>Tour Packages ({packages.length})</h3>
                    </div>

                    {packagesLoading ? (
                        <div className={styles.loading}>Loading packages...</div>
                    ) : packages.length > 0 ? (
                        <div className={styles.packagesGrid}>
                            {packages.map((pkg, index) => (
                                <div
                                    key={pkg.package_id || index}
                                    className={styles.packageCard}
                                    onClick={() => handlePackageClick(pkg)}
                                >
                                    <div className={styles.packageImage}>
                                        {getPackageImage(pkg) ? (
                                            <img src={getPackageImage(pkg)} alt={pkg.packageName} />
                                        ) : (
                                            <div className={styles.packagePlaceholder}>
                                                {pkg.packageName?.charAt(0)?.toUpperCase()}
                                            </div>
                                        )}
                                    </div>
                                    <div className={styles.packageInfo}>
                                        <div className={styles.packageHeader}>
                                            <h4 className={styles.packageName}>{pkg.packageName}</h4>
                                            <div className={styles.packageVerification}>
                                                <span className={`${styles.verificationBadge} ${pkg.is_verified ? styles.verified : styles.pending}`}>
                                                    {getPackageVerificationStatus(pkg)}
                                                </span>
                                            </div>
                                        </div>
                                        <p className={styles.packageDescription}>
                                            {getPackageDescription(pkg)}
                                        </p>
                                        <div className={styles.packageDetails}>
                                            <div className={styles.packageDetail}>
                                                <span className={styles.detailLabel}>Duration:</span>
                                                <span className={styles.detailValue}>{getPackageDuration(pkg)}</span>
                                            </div>
                                            <div className={styles.packageDetail}>
                                                <span className={styles.detailLabel}>Price:</span>
                                                <span className={styles.detailValue}>{formatPrice(pkg.price)}</span>
                                            </div>
                                            <div className={styles.packageDetail}>
                                                <span className={styles.detailLabel}>Category:</span>
                                                <span className={styles.detailValue}>{getPackageCategory(pkg)}</span>
                                            </div>
                                            <div className={styles.packageDetail}>
                                                <span className={styles.detailLabel}>Max People:</span>
                                                <span className={styles.detailValue}>{getMaxPeople(pkg)}</span>
                                            </div>
                                        </div>
                                        <div className={styles.packageRatings}>
                                            <span className={styles.rating}>⭐ {getPackageRating(pkg)}</span>
                                            <span className={styles.reviews}>({getPackageReviews(pkg)} reviews)</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className={styles.noPackages}>
                            No packages available for this operator.
                        </div>
                    )}
                </div>

                {/* Additional Information Section */}
                {(operator.about_text1 || operator.about_text2 || operator.experience_text) && (
                    <div className={styles.additionalInfo}>
                        <h3 className={styles.detailTitle}>Additional Information</h3>
                        {operator.about_text1 && (
                            <div className={styles.additionalSection}>
                                <h4>About</h4>
                                <p>{operator.about_text1}</p>
                            </div>
                        )}
                        {operator.about_text2 && (
                            <div className={styles.additionalSection}>
                                <p>{operator.about_text2}</p>
                            </div>
                        )}
                        {operator.experience_text && (
                            <div className={styles.additionalSection}>
                                <h4>Experience</h4>
                                <p>{operator.experience_text}</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Package Detail Modal */}
            {showPackageModal && selectedPackage && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modalContent}>
                        <div className={styles.modalHeader}>
                            <div className={styles.modalTitleSection}>
                                <h2 className={styles.modalTitle}>{selectedPackage.packageName}</h2>
                                <div className={styles.modalVerification}>
                                    <span className={`${styles.verificationBadge} ${selectedPackage.is_verified ? styles.verified : styles.pending}`}>
                                        {getPackageVerificationStatus(selectedPackage)}
                                    </span>
                                </div>
                            </div>
                            <div className={styles.modalHeaderActions}>
                                <button
                                    className={styles.closeButton}
                                    onClick={closePackageModal}
                                >
                                    ×
                                </button>
                            </div>
                        </div>

                        {/* Modal Message Alert */}
                        {modalMessage.text && (
                            <div className={`${styles.modalAlert} ${styles[modalMessage.type]}`}>
                                {modalMessage.text}
                            </div>
                        )}

                        <div className={styles.modalBody}>
                            {/* Package Image */}
                            <div className={styles.modalImageSection}>
                                {getPackageImage(selectedPackage) ? (
                                    <img
                                        src={getPackageImage(selectedPackage)}
                                        alt={selectedPackage.packageName}
                                        className={styles.modalImage}
                                    />
                                ) : (
                                    <div className={styles.modalPlaceholder}>
                                        {selectedPackage.packageName?.charAt(0)?.toUpperCase()}
                                    </div>
                                )}
                            </div>

                            {/* Package Overview */}
                            <div className={styles.packageOverview}>
                                <div className={styles.overviewGrid}>
                                    <div className={styles.overviewItem}>
                                        <span className={styles.overviewLabel}>Price</span>
                                        <span className={styles.overviewValue}>{formatPrice(selectedPackage.price)}</span>
                                    </div>
                                    <div className={styles.overviewItem}>
                                        <span className={styles.overviewLabel}>Duration</span>
                                        <span className={styles.overviewValue}>{getPackageDuration(selectedPackage)}</span>
                                    </div>
                                    <div className={styles.overviewItem}>
                                        <span className={styles.overviewLabel}>Category</span>
                                        <span className={styles.overviewValue}>{getPackageCategory(selectedPackage)}</span>
                                    </div>
                                    <div className={styles.overviewItem}>
                                        <span className={styles.overviewLabel}>Max People</span>
                                        <span className={styles.overviewValue}>{getMaxPeople(selectedPackage)}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Package Description */}
                            <div className={styles.modalSection}>
                                <h3 className={styles.sectionTitle}>Description</h3>
                                <p className={styles.modalDescription}>
                                    {getPackageDescription(selectedPackage)}
                                </p>
                            </div>

                            {/* Package Location */}
                            <div className={styles.modalSection}>
                                <h3 className={styles.sectionTitle}>Location</h3>
                                <p className={styles.locationText}>
                                    {getPackageLocation(selectedPackage)}
                                </p>
                            </div>

                            {/* Package Itinerary - Days */}
                            {selectedPackage.days && selectedPackage.days.length > 0 && (
                                <div className={styles.modalSection}>
                                    <h3 className={styles.sectionTitle}>Itinerary</h3>
                                    <div className={styles.daysList}>
                                        {selectedPackage.days.map((day, index) => (
                                            <div key={day.id || index} className={styles.dayItem}>
                                                <div className={styles.dayHeader}>
                                                    <h4 className={styles.dayTitle}>Day {day.day_number}</h4>
                                                    {day.night_number && (
                                                        <span className={styles.nightBadge}>Night {day.night_number}</span>
                                                    )}
                                                </div>

                                                {day.day_description && (
                                                    <p className={styles.dayDescription}>{day.day_description}</p>
                                                )}

                                                {day.start_location && (
                                                    <div className={styles.dayDetail}>
                                                        <span className={styles.detailLabel}>Location:</span>
                                                        <span className={styles.detailValue}>{day.start_location}</span>
                                                    </div>
                                                )}

                                                {day.stay_type && (
                                                    <div className={styles.dayDetail}>
                                                        <span className={styles.detailLabel}>Accommodation:</span>
                                                        <span className={styles.detailValue}>
                                                            {day.property_name ? `${day.property_name} (${day.stay_type})` : day.stay_type}
                                                        </span>
                                                    </div>
                                                )}

                                                {/* Meals for this day */}
                                                {day.meals && day.meals.length > 0 && (
                                                    <div className={styles.mealsSection}>
                                                        <span className={styles.mealsLabel}>Meals Included:</span>
                                                        <div className={styles.mealsList}>
                                                            {day.meals.map((meal, mealIndex) => (
                                                                <span key={meal.id || mealIndex} className={styles.mealTag}>
                                                                    {getMealTypeDisplay(meal.meal_type)}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Package Ratings */}
                            <div className={styles.modalSection}>
                                <h3 className={styles.sectionTitle}>Ratings & Reviews</h3>
                                <div className={styles.ratingsOverview}>
                                    <div className={styles.ratingDisplay}>
                                        <span className={styles.ratingStars}>⭐ {getPackageRating(selectedPackage)}</span>
                                        <span className={styles.reviewCount}>({getPackageReviews(selectedPackage)} reviews)</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={styles.modalFooter}>
                            <div className={styles.modalActions}>
                                <div className={styles.packageActions}>
                                    {!selectedPackage.is_verified ? (
                                        <button
                                            onClick={handleVerifyPackage}
                                            disabled={packageVerifying}
                                            className={styles.verifyPackageButton}
                                        >
                                            {packageVerifying ? 'Verifying...' : 'Verify Package'}
                                        </button>
                                    ) : (
                                        <button
                                            onClick={handleUnverifyPackage}
                                            disabled={packageVerifying}
                                            className={styles.unverifyPackageButton}
                                        >
                                            {packageVerifying ? 'Updating...' : 'Unverify Package'}
                                        </button>
                                    )}
                                </div>
                                {/* <button
                                    className={styles.closeModalButton}
                                    onClick={closePackageModal}
                                >
                                    Close
                                </button> */}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default SpecificOperator